import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { Select } from '../ui/Select';

export interface DataGridPaginationProps {
  page: number;
  pageSize: number;
  totalRows: number;
  filteredCount?: number;
  isFiltered?: boolean;
  onPageChange: (newPage: number) => void;
  onPageSizeChange?: (newSize: number) => void;
  selectedCount?: number;
  pageSizeOptions?: number[];
  unitLabel?: string;
  showPageSize?: boolean;
  className?: string;
}

export const DataGridPagination: React.FC<DataGridPaginationProps> = ({
  page,
  pageSize,
  totalRows,
  filteredCount,
  isFiltered = false,
  onPageChange,
  onPageSizeChange,
  selectedCount = 0,
  pageSizeOptions = [25, 50, 100, 250],
  unitLabel = 'rows',
  showPageSize = true,
  className = '',
}) => {
  const effectiveTotal = isFiltered && filteredCount !== undefined ? filteredCount : totalRows;
  const totalPages = Math.max(1, Math.ceil(effectiveTotal / pageSize));
  const startRow = effectiveTotal === 0 ? 0 : (page - 1) * pageSize + 1;
  const endRow = Math.min(effectiveTotal, page * pageSize);

  const resolvedOptions = pageSizeOptions.includes(pageSize)
    ? pageSizeOptions
    : [...pageSizeOptions, pageSize].sort((a, b) => a - b);

  return (
    <div
      className={`min-h-10 h-auto py-1.5 bg-cf-tableHeader dark:bg-[#2e323c] border-t border-cf-border dark:border-[#2e323c] px-3 flex items-center justify-between gap-2 flex-wrap text-xs text-cf-textSecondary ${className}`.trim()}
    >
      {/* Left: Row Counts & Selected count */}
      <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
        <span className="tabular-nums">
          <span className="hidden sm:inline">Showing </span>
          <strong className="text-cf-textPrimary">{startRow}</strong>
          <span className="text-cf-textMuted"> - </span>
          <strong className="text-cf-textPrimary">{endRow}</strong> of{' '}
          <strong className="text-cf-textPrimary">{effectiveTotal.toLocaleString()}</strong>
          <span className="hidden sm:inline"> {unitLabel}</span>
          {isFiltered && (
            <span className="text-cf-textMuted ml-1 text-xs">
              <span className="hidden sm:inline">(filtered from </span>
              <span className="sm:hidden">/ </span>
              {totalRows.toLocaleString()}
              <span className="hidden sm:inline">)</span>
            </span>
          )}
        </span>

        {selectedCount > 0 && (
          <span className="px-1.5 py-0.5 rounded bg-cf-orangeMuted text-cf-orange border border-cf-orange/30 font-medium text-xs">
            {selectedCount} selected
          </span>
        )}
      </div>

      {/* Right: Page Size & Pagination Controls */}
      <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
        {/* Page Size Selector */}
        {showPageSize && onPageSizeChange && (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="hidden sm:inline">Rows per page:</span>
            <span className="sm:hidden">Rows:</span>
            <div className="w-20">
              <Select
                size="xs"
                value={pageSize}
                onChange={(e) => {
                  onPageSizeChange(Number(e.target.value));
                  onPageChange(1);
                }}
                options={resolvedOptions.map((opt) => ({
                  value: opt,
                  label: String(opt),
                }))}
              />
            </div>
          </div>
        )}

        {/* Page Nav Buttons */}
        <div className="flex items-center gap-1">
          <span className="tabular-nums">
            <span className="hidden sm:inline">Page </span>
            <strong>{page}</strong> / <strong>{totalPages}</strong>
          </span>

          <div className="flex items-center gap-0.5 ml-1 sm:ml-2">
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={page <= 1}
              onClick={() => onPageChange(1)}
              className="text-cf-textSecondary hover:text-cf-textPrimary hidden sm:inline-flex"
              title="First Page"
            >
              <ChevronsLeft size={14} />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              className="text-cf-textSecondary hover:text-cf-textPrimary"
              title="Previous Page"
            >
              <ChevronLeft size={14} />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
              className="text-cf-textSecondary hover:text-cf-textPrimary"
              title="Next Page"
            >
              <ChevronRight size={14} />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              disabled={page >= totalPages}
              onClick={() => onPageChange(totalPages)}
              className="text-cf-textSecondary hover:text-cf-textPrimary hidden sm:inline-flex"
              title="Last Page"
            >
              <ChevronsRight size={14} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
